import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 select-none',
  {
    variants: {
      variant: {
        default: 'bg-brand text-brand-inverse hover:bg-brand-hover shadow-sm font-semibold',
        navy: 'bg-brand-ink text-brand-inverse hover:bg-brand-hover shadow-sm font-semibold',
        destructive: 'bg-status-error text-white hover:bg-red-700 shadow-sm font-semibold',
        outline: 'border border-brand-border bg-surface hover:bg-brand-surface text-brand-ink shadow-sm',
        secondary: 'bg-brand-tint text-brand-ink hover:bg-[#C2DFD7] font-semibold',
        ghost: 'hover:bg-brand-tint/40 hover:text-brand-ink text-brand-muted',
        link: 'text-brand underline-offset-4 hover:underline font-semibold',
        lime: 'bg-brand-lime text-brand-ink hover:brightness-95 shadow-sm font-bold',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';
